import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Phone, ArrowLeft, Star, Truck, Shield, RefreshCw,
  Loader2, MessageCircle, MapPin, Package, Zap, CreditCard
} from 'lucide-react';
import useMetaPixel from '../hooks/useMetaPixel';
import { trackWaConversion } from '../utils/trackConversion';
import { trackViewItem } from '../utils/ga4';
import { updateSEO, injectProductJsonLd } from '../utils/seoUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ─── Freight Calculator ─── */

// Zone 1: Regional (até ~750km de Palmas-TO)
// Zone 2: Estendida (Nordeste, Sudeste, Sul e demais)
const FREIGHT_ZONES = {
  TO: 1, GO: 1, BA: 1, MA: 1, PA: 1, MT: 1,
};

// Tabela PAC Correios por faixa de peso
const FREIGHT_TABLE = {
  1: { // Regional
    rows: [
      { label: 'Até 1 kg',   range: 'R$ 22 – 32',  days: '4–7 dias úteis' },
      { label: '1 – 3 kg',   range: 'R$ 28 – 45',  days: '4–7 dias úteis' },
      { label: '3 – 7 kg',   range: 'R$ 40 – 70',  days: '5–8 dias úteis' },
      { label: '7 – 15 kg',  range: 'R$ 65 – 110', days: '5–8 dias úteis' },
      { label: '15 – 30 kg', range: 'R$ 110 – 200',days: '6–10 dias úteis' },
    ],
    minPrice: 22,
  },
  2: { // Estendida
    rows: [
      { label: 'Até 1 kg',   range: 'R$ 28 – 40',  days: '6–10 dias úteis' },
      { label: '1 – 3 kg',   range: 'R$ 33 – 55',  days: '6–10 dias úteis' },
      { label: '3 – 7 kg',   range: 'R$ 50 – 85',  days: '7–11 dias úteis' },
      { label: '7 – 15 kg',  range: 'R$ 85 – 145', days: '7–11 dias úteis' },
      { label: '15 – 30 kg', range: 'R$ 145 – 260',days: '8–14 dias úteis' },
    ],
    minPrice: 28,
  },
};

const CEP_TO_STATE = [
  [1000000, 19999999, 'SP'], [20000000, 28999999, 'RJ'], [29000000, 29999999, 'ES'],
  [30000000, 39999999, 'MG'], [40000000, 48999999, 'BA'], [49000000, 49999999, 'SE'],
  [50000000, 56999999, 'PE'], [57000000, 57999999, 'AL'], [58000000, 58999999, 'PB'],
  [59000000, 59999999, 'RN'], [60000000, 63999999, 'CE'], [64000000, 64999999, 'PI'],
  [65000000, 65999999, 'MA'], [66000000, 68899999, 'PA'], [68900000, 68999999, 'AP'],
  [69000000, 69299999, 'AM'], [69300000, 69399999, 'RR'], [69400000, 69899999, 'AM'],
  [69900000, 69999999, 'AC'], [70000000, 72799999, 'DF'], [72800000, 76799999, 'GO'],
  [76800000, 76999999, 'TO'], [77000000, 77999999, 'TO'],
  [78000000, 78899999, 'MT'], [78900000, 78999999, 'MS'], [79000000, 79999999, 'MS'],
  [80000000, 87999999, 'PR'], [88000000, 89999999, 'SC'], [90000000, 99999999, 'RS'],
];

function getStateFromCep(cep) {
  const num = parseInt(cep.replace(/\D/g, ''), 10);
  if (isNaN(num)) return null;
  for (const [min, max, state] of CEP_TO_STATE) {
    if (num >= min && num <= max) return state;
  }
  return null;
}

const FreightCalculator = () => {
  const [cep, setCep] = useState('');
  const [result, setResult] = useState(null);

  const formatCep = (v) => {
    const digits = v.replace(/\D/g, '').slice(0, 8);
    return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  };

  const calculate = () => {
    const state = getStateFromCep(cep);
    if (!state) {
      setResult({ error: 'CEP inválido. Verifique e tente novamente.' });
      return;
    }
    const zoneNum = FREIGHT_ZONES[state] || 2;
    setResult({ state, zoneNum });
  };

  return (
    <div className="mt-6 bg-gray-50 rounded-xl p-4" data-testid="freight-calculator">
      <div className="flex items-center gap-2 mb-3">
        <Truck className="w-5 h-5 text-[#162289]" />
        <h4 className="font-semibold text-gray-800 text-sm">Calcular frete</h4>
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={cep}
          onChange={(e) => { setCep(formatCep(e.target.value)); setResult(null); }}
          placeholder="00000-000"
          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#162289]/30 focus:border-[#162289]"
          maxLength={9}
          data-testid="cep-input"
        />
        <button
          onClick={calculate}
          disabled={cep.replace(/\D/g, '').length < 8}
          className="px-4 py-2 bg-[#162289] text-white text-sm font-semibold rounded-lg hover:bg-[#1a2fa0] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          data-testid="calc-freight-btn"
        >
          Calcular
        </button>
      </div>
      {result && (
        <div className="mt-3 text-sm" data-testid="freight-result">
          {result.error ? (
            <p className="text-red-500">{result.error}</p>
          ) : (
            <div className="space-y-3">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-blue-800 font-semibold text-xs uppercase tracking-wide">
                    📦 Frete estimado — Correios PAC
                  </p>
                  <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                    {result.zoneNum === 1 ? 'Região próxima' : 'Região estendida'}
                  </span>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-blue-600">
                      <th className="text-left py-1 font-medium">Peso</th>
                      <th className="text-center py-1 font-medium">Estimativa</th>
                      <th className="text-right py-1 font-medium">Prazo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FREIGHT_TABLE[result.zoneNum].rows.map((row, i) => (
                      <tr key={i} className="border-t border-blue-100">
                        <td className="py-1 text-blue-700">{row.label}</td>
                        <td className="py-1 text-center font-semibold text-blue-800">{row.range}</td>
                        <td className="py-1 text-right text-blue-600">{row.days}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-amber-800 text-xs">
                  ⚠️ Valor aproximado conforme peso e região. O frete <strong>não é cobrado</strong> agora — finalizamos pelo WhatsApp com o valor exato e prazo confirmado. Consulte também para <strong>Sedex</strong> (entrega mais rápida).
                </p>
                <a
                  href={`https://wa.me/556333224310?text=${encodeURIComponent('🟢 Olá! Vim do site, tenho interesse em comprar peças. Gostaria de saber o frete exato para o meu CEP.')}`}
                  target="_blank" rel="noopener noreferrer"
                  className="mt-2 flex items-center gap-1.5 text-xs text-green-700 font-semibold hover:text-green-900 transition-colors"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  Falar com atendente no WhatsApp
                </a>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Main Product Page ─── */
const PartnerProductPage = () => {
  const { slug, productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [partner, setPartner] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  useMetaPixel(slug);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productRes, partnerRes] = await Promise.all([
          fetch(`${API_URL}/api/products/${productId}`),
          fetch(`${API_URL}/api/partners/${slug}`)
        ]);
        if (productRes.ok) {
          const prod = await productRes.json();
          setProduct(prod);
          // GA4: view_item
          trackViewItem({
            id: prod.id,
            name: prod.name,
            price: prod.sale_price || prod.regular_price || 0,
            category: prod.category_name,
            partner: slug,
          });
          // SEO produto
          if (slug === 'rocha') {
            const price = prod.sale_price || prod.regular_price;
            const desc = prod.description
              ? prod.description.slice(0, 155)
              : `${prod.name} — peça para ar condicionado em Palmas-TO. ${price ? `A partir de R$ ${price.toFixed(2).replace('.', ',')}.` : 'Consulte o preço via WhatsApp.'}`;
            updateSEO({
              title: `${prod.name} | Rocha Climatização Palmas-TO`,
              description: desc,
              ogImage: prod.images?.[0] || '/rocha_yinyang.png',
              ogUrl: `/parceiros/rocha/produto/${prod.id}`,
              ogType: 'product',
            });
            injectProductJsonLd(prod, 'Rocha Climatização');
          }
        }
        if (partnerRes.ok) {
          const partnerData = await partnerRes.json();
          setPartner(partnerData);
          const relRes = await fetch(`${API_URL}/api/partners/${slug}/products?limit=8`);
          if (relRes.ok) {
            const relData = await relRes.json();
            setRelatedProducts((relData.products || []).filter(p => p.id !== productId).slice(0, 4));
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    window.scrollTo(0, 0);
  }, [slug, productId]);

  const handleWhatsApp = (customMsg) => {
    const msg = customMsg || (product
      ? `🟢 Olá! Vim do site, tenho interesse em comprar peças. Produto: ${product.name} (SKU: ${product.sku || 'N/A'})`
      : '🟢 Olá! Vim do site, tenho interesse em comprar peças.');
    window.open(`https://wa.me/556333224310?text=${encodeURIComponent(msg)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#162289]" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Produto não encontrado</h1>
          <Link to={`/parceiros/${slug}`} className="text-[#162289] hover:underline">
            Voltar para {partner?.name || 'o parceiro'}
          </Link>
        </div>
      </div>
    );
  }

  const images = (product.images && product.images.length > 0) ? product.images : ['/placeholder.png'];
  const price = product.sale_price || product.regular_price || 0;
  const hasDiscount = product.sale_price && product.regular_price && product.sale_price < product.regular_price;
  const discountPercent = hasDiscount
    ? Math.round(((product.regular_price - product.sale_price) / product.regular_price) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-white">
      {/* Partner Header */}
      <div className="bg-[#0a1555] py-3 px-6">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between">
          <Link to={`/parceiros/${slug}`} className="flex items-center gap-3 group" data-testid="back-to-partner">
            <ArrowLeft className="w-5 h-5 text-white/60 group-hover:text-white transition-colors" />
            {partner?.logo_url && (
              <img src={partner.logo_url} alt={partner?.name} className="h-9 w-auto object-contain bg-white rounded-lg p-0.5" />
            )}
            <span className="font-semibold text-white group-hover:text-white/80 transition-colors text-sm">
              {partner?.name}
            </span>
          </Link>
          <button onClick={() => handleWhatsApp()}
            className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors"
            data-testid="whatsapp-btn"
          >
            <MessageCircle className="w-4 h-4" />
            Fale Conosco
          </button>
        </div>
      </div>

      {/* Product Content */}
      <div className="max-w-[1200px] mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Gallery */}
          <div>
            <div className="aspect-square bg-gray-50 rounded-2xl overflow-hidden mb-4">
              <img
                src={images[selectedImage]}
                alt={product.name}
                className="w-full h-full object-contain p-4"
                data-testid="product-main-image"
              />
            </div>
            {images.length > 1 && (
              <div className="flex gap-3 overflow-x-auto pb-2">
                {images.map((img, i) => (
                  <button key={i} onClick={() => setSelectedImage(i)}
                    className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                      selectedImage === i ? 'border-[#162289] opacity-100' : 'border-transparent opacity-50 hover:opacity-80'
                    }`}
                    data-testid={`thumb-${i}`}
                  >
                    <img src={img} alt={`Imagem ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info */}
          <div>
            {product.category_name && (
              <span className="text-sm text-gray-400 font-medium tracking-wide uppercase">{product.category_name}</span>
            )}
            <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mt-2 leading-tight" data-testid="product-name">
              {product.name}
            </h1>

            {product.sku && (
              <p className="text-sm text-gray-400 mt-2">Código: {product.sku}</p>
            )}

            {/* Voltage badge */}
            <div className="flex items-center gap-2 mt-3">
              <span className="inline-flex items-center gap-1 bg-yellow-50 text-yellow-700 text-xs font-semibold px-2.5 py-1 rounded-lg border border-yellow-200">
                <Zap className="w-3 h-3" /> 220V
              </span>
              {product.brand && (
                <span className="inline-flex items-center bg-gray-100 text-gray-600 text-xs font-medium px-2.5 py-1 rounded-lg">
                  {product.brand}
                </span>
              )}
            </div>

            {/* Rating */}
            {product.reviews_count > 0 && (
              <div className="flex items-center gap-2 mt-3">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`w-4 h-4 ${i < Math.floor(product.average_rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`} />
                  ))}
                </div>
                <span className="text-sm text-gray-500">({product.reviews_count})</span>
              </div>
            )}

            {/* Price */}
            <div className="mt-6 bg-gray-50 rounded-2xl p-5">
              {price > 0 ? (
                <>
                  <div className="flex items-baseline gap-3">
                    <span className="text-3xl font-bold text-gray-900" data-testid="product-price">
                      R$ {price.toFixed(2)}
                    </span>
                    {hasDiscount && (
                      <>
                        <span className="text-lg text-gray-400 line-through">R$ {product.regular_price.toFixed(2)}</span>
                        <span className="bg-green-100 text-green-700 px-2.5 py-0.5 rounded-full text-sm font-bold">-{discountPercent}%</span>
                      </>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    até 10x de R$ {(price / 10).toFixed(2)} <span className="text-xs text-gray-400">(juros da operadora)</span>
                  </p>
                </>
              ) : (
                <div>
                  <span className="text-xl font-bold text-gray-500">Preço sob consulta</span>
                  <p className="text-sm text-gray-400 mt-1">Cotação diária - entre em contato via WhatsApp</p>
                </div>
              )}
              {product.in_stock ? (
                <p className="text-green-600 font-medium mt-2 text-sm">Em estoque</p>
              ) : (
                <p className="text-amber-600 font-medium mt-2 text-sm">Consultar disponibilidade</p>
              )}
            </div>

            {/* Buy & WhatsApp CTAs */}
            <div className="mt-5 space-y-3">
              {price > 0 && (
                <button onClick={() => {
                  const imgUrl = images[0] !== '/placeholder.png' ? images[0] : '';
                  navigate(`/parceiros/${slug}/checkout?produto=${product.id}&nome=${encodeURIComponent(product.name)}&preco=${price}&imagem=${encodeURIComponent(imgUrl)}&qtd=1`);
                }}
                  className="w-full flex items-center justify-center gap-3 py-3.5 bg-[#009ee3] hover:bg-[#008bcf] text-white rounded-xl font-semibold text-base transition-colors"
                  data-testid="buy-now-btn"
                >
                  <CreditCard className="w-5 h-5" />
                  Comprar agora
                </button>
              )}
              <button onClick={() => handleWhatsApp()}
                className="w-full flex items-center justify-center gap-3 py-3.5 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold text-base transition-colors"
                data-testid="whatsapp-cta"
              >
                <MessageCircle className="w-5 h-5" />
                {price > 0 ? `Falar com ${partner?.name || 'Vendedor'}` : 'Cotar via WhatsApp'}
              </button>
              <a href="tel:6333224310"
                className="w-full flex items-center justify-center gap-2 py-3 border-2 border-gray-200 text-gray-600 rounded-xl font-medium hover:border-gray-300 transition-colors"
                data-testid="phone-cta"
              >
                <Phone className="w-5 h-5" />
                Ligar: (63) 3322-4310
              </a>
            </div>

            {/* Benefits */}
            <div className="mt-6 grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-gray-50 rounded-xl">
                <MapPin className="w-5 h-5 mx-auto text-[#162289] mb-1.5" />
                <span className="text-xs text-gray-600 leading-tight block font-medium">Tocantins e região</span>
                <span className="text-[10px] text-gray-400 block mt-0.5">Demais estados sob consulta</span>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-xl">
                <Shield className="w-5 h-5 mx-auto text-[#162289] mb-1.5" />
                <span className="text-xs text-gray-600 leading-tight block font-medium">Garantia do fabricante</span>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-xl">
                <RefreshCw className="w-5 h-5 mx-auto text-[#162289] mb-1.5" />
                <span className="text-xs text-gray-600 leading-tight block font-medium">Troca em até 30 dias</span>
                <span className="text-[10px] text-gray-400 block mt-0.5">Conforme condições e garantia</span>
              </div>
            </div>

            {/* Freight Calculator */}
            <FreightCalculator />

            {/* Description */}
            {product.description && (
              <div className="mt-6 border-t border-gray-100 pt-5">
                <h3 className="font-semibold text-gray-900 mb-3">Descrição do Produto</h3>
                <div className="text-gray-600 leading-relaxed text-sm whitespace-pre-line">{product.description}</div>
              </div>
            )}

            {/* Specs */}
            <div className="mt-5 border-t border-gray-100 pt-5">
              <h3 className="font-semibold text-gray-900 mb-3">Especificações</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-gray-50 rounded-lg px-3 py-2">
                  <span className="text-gray-400 text-xs">Voltagem</span>
                  <p className="text-gray-800 font-medium">220V</p>
                </div>
                {product.sku && (
                  <div className="bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-gray-400 text-xs">Código/SKU</span>
                    <p className="text-gray-800 font-medium">{product.sku}</p>
                  </div>
                )}
                {product.brand && (
                  <div className="bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-gray-400 text-xs">Marca</span>
                    <p className="text-gray-800 font-medium">{product.brand}</p>
                  </div>
                )}
                <div className="bg-gray-50 rounded-lg px-3 py-2">
                  <span className="text-gray-400 text-xs">Garantia</span>
                  <p className="text-gray-800 font-medium">Do fabricante</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Partner Promotional Banner */}
        {partner?.slug === 'rocha' && (
          <div className="mt-12" data-testid="partner-promo-banner">
            <img
              src="/rocha_banner_lg.jpg"
              alt="Rocha Climatização - A Maior Distribuidora de Peças LG do Tocantins"
              className="w-full rounded-2xl shadow-lg object-cover"
            />
          </div>
        )}

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-12 border-t border-gray-100 pt-10">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Outros produtos de {partner?.name}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {relatedProducts.map((p) => (
                <Link key={p.id} to={`/parceiros/${slug}/produto/${p.id}`}
                  className="group bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all"
                  data-testid={`related-product-${p.id}`}
                >
                  <div className="aspect-square bg-white p-3 flex items-center justify-center">
                    {p.images && p.images.length > 0 ? (
                      <img src={p.images[0]} alt={p.name} className="max-h-full max-w-full object-contain group-hover:scale-105 transition-transform" />
                    ) : (
                      <Package className="w-12 h-12 text-gray-300" />
                    )}
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-medium text-gray-800 line-clamp-2 leading-tight">{p.name}</h3>
                    {(p.sale_price || p.regular_price) > 0 ? (
                      <p className="text-sm font-bold text-[#162289] mt-1.5">R$ {(p.sale_price || p.regular_price).toFixed(2)}</p>
                    ) : (
                      <p className="text-xs text-green-600 font-medium mt-1.5 flex items-center gap-1">
                        <MessageCircle className="w-3 h-3" /> Cotar via WhatsApp
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-[#0a1555] py-6">
        <div className="max-w-[1200px] mx-auto px-6 text-center">
          <p className="text-white/40 text-sm">
            &copy; {new Date().getFullYear()} {partner?.name}. Todos os direitos reservados. |{' '}
            <a href="/brasil" className="text-white/50 hover:text-white/70 transition-colors">Powered by AUG</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PartnerProductPage;
